import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * The three states every asynchronous surface has, given one shape.
 *
 * These exist because the first cut of both apps handled them ad hoc: Overview
 * had a loading line and an error line, Accounts and Audit had neither, and
 * Limits had nothing to load at all. Whether a page tells you it is working,
 * or empty, or broken should not depend on who wrote it.
 */

interface StateProps {
  title?: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

function StateShell({ icon, title, description, action, className, role }: StateProps & { role?: string }) {
  return (
    <div
      // Without this the swap between loading, loaded and failed is silent to a
      // screen reader: the content simply changes underneath the user. These
      // components replaced every per-page loading and error line in both apps,
      // so getting it wrong here gets it wrong everywhere.
      role={role}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-6 py-12 text-center',
        className,
      )}
    >
      {icon}
      {title && <p className="text-sm font-medium">{title}</p>}
      {description && (
        <p className="max-w-md text-sm text-muted-foreground">{description}</p>
      )}
      {action}
    </div>
  );
}

export function LoadingState({ title = 'Loading…', ...props }: StateProps) {
  return (
    <StateShell
      icon={<Loader2 className="size-5 animate-spin text-muted-foreground" />}
      title={title}
      // Polite: it should not interrupt whatever is being read.
      role="status"
      {...props}
    />
  );
}

export function EmptyState({ title = 'Nothing here yet', ...props }: StateProps) {
  return <StateShell icon={<Inbox className="size-5 text-muted-foreground" />} title={title} {...props} />;
}

export function ErrorState({ title = 'Something went wrong', ...props }: StateProps) {
  return (
    <StateShell
      icon={<AlertTriangle className="size-5 text-destructive" />}
      title={title}
      // Assertive: a failed load changes what the user can believe about the
      // page, so it is worth interrupting for.
      role="alert"
      className="border-destructive/40"
      {...props}
    />
  );
}
